/**
 * Copyright (C), 2012-2021, Sichuan Xiaoka Technology Co., Ltd.
 * 工具包
 * */
import sha256 from "js-sha256";
import Crypto from "crypto-js";
import { api as viewerApi } from "v-viewer";

import { Message } from "element-ui";
const rs = require("jsrsasign");

/**
 * RSA私钥配置
 * 安全说明：私钥从环境变量读取，生产环境请确保配置 VUE_APP_RSA_PRIVATE_KEY
 * 如未配置环境变量，则使用默认开发私钥（仅限开发环境使用）
 */
const DEFAULT_DEV_PRIVATE_KEY = `-----BEGIN PRIVATE KEY-----
MIICdwIBADANBgkqhkiG9w0BAQEFAASCAmEwggJdAgEAAoGBALXupdIruoj5JgRARDVDBSrZv5vV4lBTQx1hJPP4E9IwB8F0y8mg5tOamEiMkWtvQfiwajqZQpKBdMeK1k3aMONzCfU66R/UuqdaFuIoH7/ynIjLaxnw7Sd+f5ktY7V2p7P3C60B0ncZtdgD3ZcsAgDJeld8y+hSuYHdBVV8n5XTAgMBAAECgYAXOK/HpLc5dnuovT83gSdusb5oOKObVxVQ8a5SyY5WHLVQtwSv5q6vwaLAV9jT2q6T/IiClD9QDeAG/CygYUIt/hhF//mD390V1hMRb1c10qZvvTcoT6IeGVAQTeVuI7n2YDFK6sZOVuZ60LsSJZ6+aaCXPDDrAOn6my6H/QkmAQJBAP2ckrhLEtrhydp9lNKVMC5sKuCvHOU/Bus7YUqh/JvPTaGjvc/UYZ9BG09F9yNHjZcPH6kbSC9jARJoQHu3MNMCQQC3pUOncs/wyiduneCGhJ6VNE2GJKMTvxcDhBHBOSL71T6THPuGyd7YK4qxSmKXYCMwAEMoLp1zNs/1VQkkfucBAkEA6dlL+J9Q3X9A7zTI23wxFz0qNE+D52a8bH8ucPD4ynrWbGtdhJujAIO6ONicz20VA+swGHBNmVRDEsQKYYlDfQJBAJVhc/e4N5b5CTw8Zc9l/w0E/253TMOaM6Ejg8G1tVQ98GmAMMVCXede+7Ec/bd+dwDrpbUAvghi9KPt6MLsCwECQBGAhRk9fvbIM3X8ZkvcojfiSInsDs96Jj2EdQla7OE+VyfQqo0b4Tq/wrwT8jcO6+sfpZH7U7gZ5RNjgXcV2lA=
-----END PRIVATE KEY-----
`;

// 优先从环境变量读取私钥，否则使用默认开发私钥
const privateKeyString = process.env.VUE_APP_RSA_PRIVATE_KEY || DEFAULT_DEV_PRIVATE_KEY;

// 生产环境检查：如果是生产环境且未配置环境变量，输出警告
if (process.env.NODE_ENV === 'production' && !process.env.VUE_APP_RSA_PRIVATE_KEY) {
  console.warn('[Security Warning] RSA private key is using default development key in production environment. Please configure VUE_APP_RSA_PRIVATE_KEY environment variable.');
}
const utils = {
  // AES加密
  AesEncryptFn(plaintText, key, iv) {
    key = Crypto.enc.Utf8.parse(key);
    iv = Crypto.enc.Utf8.parse(iv);
    let encryptedData = Crypto.AES.encrypt(plaintText, key, {
      mode: Crypto.mode.CBC,
      padding: Crypto.pad.Pkcs7,
      iv: iv,
    });
    return encryptedData.ciphertext.toString(Crypto.enc.Base64);
  },
  // AES解密
  AesDecryptFn(data, key, iv) {
    key = Crypto.enc.Utf8.parse(key);
    iv = Crypto.enc.Utf8.parse(iv);
    let decrypted = Crypto.AES.decrypt(data, key, {
      iv: iv,
      mode: Crypto.mode.CBC,
      padding: Crypto.pad.Pkcs7,
    });
    return decrypted.toString(Crypto.enc.Utf8);
  },
  RSA_SIGN(content) {
    const key = rs.KEYUTIL.getKey(privateKeyString);
    // 创建 Signature 对象，设置签名编码算法
    const signature = new rs.KJUR.crypto.Signature({ alg: "MD5withRSA" });
    // 初始化
    signature.init(key);

    signature.updateString(content);
    // 生成密文
    const originSign = signature.sign();
    return rs.hextob64(originSign);
  },
  uuid() {
    let s = [];
    const hexDigits = "0123456789abcdef";
    for (let i = 0; i < 36; i++) {
      s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
    }
    s[14] = "4"; // bits 12-15 of the time_hi_and_version field to 0010
    s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
    s[8] = s[13] = s[18] = s[23] = "-";
    return s.join("");
  },
  getWeek(date) {
    let time = date;
    const week = ["周天", "周一", "周二", "周三", "周四", "周五", "周六"];
    if (String(date).length === 10) {
      time = time * 1000;
    }
    return week[new Date(time).getDay()];
  },
  /**
   * 对象深拷贝
   * @param obj {Object} 对象
   */
  deepCopyFn(obj) {
    if (obj) {
      return JSON.parse(JSON.stringify(obj));
    }
    return obj;
  },
  /**
   * sha256 加密
   * @param text
   * @returns {String}
   */
  sha256Fn(text) {
    return sha256(text);
  },
  /**
   * 数字前补0
   * @param num {Number|String} 要补0的数据
   * @return {string}
   */
  zfill(num) {
    num = Number(num);
    return num < 10 ? "0" + num : num;
  },
  /**
   * 时间戳转换
   * @param timestamp
   * @param format
   */
  timeFn(timestamp, format = "YYYY/MM/DD HH:mm:SS") {
    if (!timestamp) {
      return "-";
    }
    if (String(timestamp).length <= 10) {
      timestamp = timestamp * 1000;
    }
    let date = new Date(timestamp);
    let year = date.getFullYear();
    let month = this.zfill(date.getMonth() + 1);
    let day = this.zfill(date.getDate());
    let hour = this.zfill(date.getHours());
    let minute = this.zfill(date.getMinutes());
    let second = this.zfill(date.getSeconds());
    format = format.replace(/YYYY/, year);
    format = format.replace(/MM/, month);
    format = format.replace(/DD/, day);
    format = format.replace(/HH/, hour);
    format = format.replace(/mm/, minute);
    format = format.replace(/SS/, second);
    return format;
  },
  /**
   * 查看大图功能
   */
  largeImgFn(src) {
    if (!src) return;
    let urls = [];
    if (Array.isArray(src)) {
      src.forEach((item) => {
        urls.push(window.configure.qiniuUrl + item + "?imageView2/1");
      });
    } else {
      urls.push(window.configure.qiniuUrl + src + "?imageView2/1");
    }
    viewerApi({ images: urls });

    // let oDiv = document.createElement("div");
    // let oInner = document.createElement("div");
    // let oImg = document.createElement("img");
    // oDiv.className = "largeImg";
    // oDiv.title = "单击鼠标左键关闭查看大图";
    // oInner.className = "largeImgInner";
    // oImg.src = window.configure.qiniuUrl + src + "?imageView2/1";
    // oInner.appendChild(oImg);
    // oDiv.appendChild(oInner);
    // document.body.appendChild(oDiv);
    // oDiv.addEventListener(
    //   "click",
    //   function () {
    //     oDiv.style.display = "none";
    //   },
    //   true
    // );
  },
  getGender(id) {
    if (!id) {
      return new Error("身份证不能为空");
    }
    // 身份证号的倒数第二位，奇数为男性，偶数为女性
    var genderCode = parseInt(id.charAt(id.length - 2));
    return genderCode % 2 === 0 ? "女" : "男";
  },
  getCityName(data, citycode, level = "city") {
    if (!Array.isArray(data)) {
      new Error("data must be an array");
    }
    for (const province of data) {
      if (province.citycode === citycode) {
        return province.name;
      }

      if (province.districts) {
        for (const district of province.districts) {
          if (district.citycode === citycode) {
            if (level === "city") {
              return district.name;
            } else if(level === 'province'){
              return province.name
            } else {
              return province.name + " / " + district.name;
            }
          }
        }
      }
    }

    return "";
  },
  // 根据身份证号计算年龄
  getAgeByIdCard(idCard) {
    // 如果用户身份证号码为 undefined 或者为空，则返回 null
    if (!idCard) {
      return null;
    }

    // 获取出生日期
    const yearBirth = +idCard.substring(6, 10);
    const monthBirth = +idCard.substring(10, 12);
    const dayBirth = +idCard.substring(12, 14);

    // 获取当前年月日并计算年龄
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // 注意月份是从0开始的
    const currentDay = currentDate.getDate();

    let age = currentYear - yearBirth;
    // 如果当前日期还未到生日，则年龄减一
    if (
      currentMonth < monthBirth ||
      (currentMonth === monthBirth && currentDay < dayBirth)
    ) {
      age--;
    }

    return age;
  },

  /**
   * 双击复制指定元素的文本内容。
   * 如果浏览器支持 Clipboard API，则优先使用，否则会使用备用方法。
   * 复制成功后会显示提示信息。
   * @param {Event} event - 双击事件对象，用于获取目标元素。
   * @param {string} [tips='复制成功'] - 复制成功后显示的提示信息。
   */
  async dblclickCopy(event, tips = '复制成功') {
    const el = event.currentTarget;
    try {
      // 使用 innerText 获取元素的可见文本内容
      await utils.copyText(el.innerText);
    } catch (error) {
      console.error('复制失败:', error);
      // copyText 函数内部已经处理了错误消息，这里可以不再次提示
    }
  },

  /**
   * 复制文本到剪贴板。
   * 优先使用现代 Clipboard API，如果不可用则回退到传统方法。
   * @param {string} text - 要复制的文本内容。
   * @returns {Promise<boolean>} - 一个 Promise，解析为 true 表示复制成功，解析为 false 或 rejected 表示复制失败。
   */
  copyText(text) {
    return new Promise((resolve, reject) => {
      // 优先使用现代 Clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          Message.success('复制成功');
          resolve(true);
        }).catch(() => {
          // 如果 Clipboard API 失败，使用传统方法
          this.fallbackCopyText(text, resolve, reject);
        });
      } else {
        // 在不支持 Clipboard API 的环境下使用传统方法
        this.fallbackCopyText(text, resolve, reject);
      }
    });
  },

  fallbackCopyText(text, resolve, reject) {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (successful) {
        Message.success('复制成功');
        resolve(true);
      } else {
        Message.error('复制失败');
        reject(new Error('复制失败'));
      }
    } catch (err) {
      Message.error('复制失败');
      reject(err);
    }
  },

  daysBetweenDates(date1, date2) {
    const oneDay = 24 * 60 * 60 * 1000; // 每天的毫秒数
    const firstDate = new Date(date1);
    const secondDate = new Date(date2);

    // 计算时间差，并将其转换为天数
    const diffDays = Math.round(Math.abs((firstDate - secondDate) / oneDay));
    return diffDays + 1;
  },
  maskPhone(phone) {
    if (!phone) {
      return '-';
    }
    return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
  },
  /**
   * 对身份证号进行掩码处理
   * @param {string} idCard - 身份证号
   * @returns {string} 掩码后的身份证号
   */
  maskIdCard(idCard) {
    if (!idCard) {
      return '-';
    }
    return idCard.replace(/(\d{6})\d{8}(\d{3}[0-9X])/, '$1****$2')
  },
  maskName(name) {
    if (!name) {
      return '-';
    }
    if (name.length === 2) {
      return name.slice(0, 1) + '*'
    } else {
      return name.slice(0, 1) + '*' + name.slice(-1)
    }
  },
  checkValidity(validity) {
    if (!validity) {
      return 999;
    }
    // validity为秒级时间戳
    const now = new Date();
    const nowTime = now.getTime();
    const validityTime = validity * 1000;
    return Math.floor((validityTime - nowTime) / (24 * 3600 * 1000));
  },
  getMinPrice(row) {
    // 从所有价格中筛选出有效的价格（大于0的数字）
    const prices = [
      row.roundCharterPrice,
      row.roundPrice,
      row.singleCharterPrice,
      row.singlePrice
    ].filter(price => price && Number(price) > 0);

    // 如果没有有效价格，返回0
    if (prices.length === 0) return 0;

    // 返回最小价格
    return Math.min(...prices);
  },
  parseUserAgent(userAgent) {
    userAgent = userAgent || navigator.userAgent || navigator.vendor || window.opera;

    let browser = { name: '', version: '' },
      os = { name: '', version: '', bit: '' },
      device = { type: '', model: '' };

    // 解析浏览器类型及版本
    if (/MicroMessenger\/(\d+\.\d+)/.test(userAgent)) {
      browser.name = 'WeChat';
      browser.version = userAgent.match(/MicroMessenger\/(\d+\.\d+)/)[1];
    } else if (/Edge\/(\d+\.\d+)/.test(userAgent)) {
      browser.name = 'Edge';
      browser.version = userAgent.match(/Edge\/(\d+\.\d+)/)[1];
    } else if (/OPR\/(\d+\.\d+)/.test(userAgent)) {
      browser.name = 'Opera';
      browser.version = userAgent.match(/OPR\/(\d+\.\d+)/)[1];
    } else if (/UCBrowser\/(\d+\.\d+)/.test(userAgent)) {
      browser.name = 'UC';
      browser.version = userAgent.match(/UCBrowser\/(\d+\.\d+)/)[1];
    } else if (/QQBrowser\/(\d+\.\d+)/.test(userAgent)) {
      browser.name = 'QQ浏览器';
      browser.version = userAgent.match(/QQBrowser\/(\d+\.\d+)/)[1];
    } else if (/Chrome\/(\d+\.\d+)/.test(userAgent)) {
      browser.name = 'Chrome';
      browser.version = userAgent.match(/Chrome\/(\d+\.\d+)/)[1];
    } else if (/Firefox\/(\d+\.\d+)/.test(userAgent)) {
      browser.name = 'Firefox';
      browser.version = userAgent.match(/Firefox\/(\d+\.\d+)/)[1];
    } else if (/Safari\/(\d+\.\d+)/.test(userAgent) && !/Chrome/.test(userAgent)) {
      browser.name = 'Safari';
      browser.version = userAgent.match(/Version\/(\d+\.\d+)/)[1];
    }

    // 解析设备类型
    if (/Mobile|Android|iPhone|iPad|iPod/.test(userAgent)) {
      device.type = 'mobile';

      // 识别具体型号
      if (/iPhone/.test(userAgent)) {
        device.model = 'iPhone';
      } else if (/iPad/.test(userAgent)) {
        device.model = 'iPad';
      } else if (/HUAWEI/.test(userAgent)) {
        const match = userAgent.match(/HUAWEI\s?([^)]+)\)/);
        device.model = match ? 'HUAWEI ' + match[1] : 'HUAWEI';
      } else if (/MI\s|Redmi/.test(userAgent)) {
        const match = userAgent.match(/(MI|Redmi)\s?([^)]+)\)/);
        device.model = match ? match[1] + ' ' + match[2] : 'Xiaomi';
      } else if (/OPPO/.test(userAgent)) {
        const match = userAgent.match(/OPPO\s?([^)]+)\)/);
        device.model = match ? 'OPPO ' + match[1] : 'OPPO';
      } else if (/vivo/.test(userAgent)) {
        const match = userAgent.match(/vivo\s?([^)]+)\)/);
        device.model = match ? 'vivo ' + match[1] : 'vivo';
      }
    } else {
      device.type = 'desktop';
    }

    // 解析操作系统
    if (/iPhone|iPad|iPod/.test(userAgent)) {
      os.name = 'iOS';
      const match = userAgent.match(/OS (\d+_\d+(_\d+)?)/);
      os.version = match ? match[1].replace(/_/g, '.') : '';

      // iOS 设备位数判断
      // iOS 11 以上版本只支持64位
      if (parseFloat(os.version) >= 11) {
        os.bit = '64';
      } else {
        // iOS 11 以下需要根据设备型号判断
        const deviceMatch = userAgent.match(/iPhone OS|iPad; CPU OS/);
        if (deviceMatch) {
          // iPhone 5s (iPhone6,1 / iPhone6,2) 及以后的机型都是64位
          // iPad Air (iPad4,1) 及以后的机型都是64位
          const model = userAgent.match(/iPhone\s*(?:OS\s+[\d_]+\s+)?(?:like\s+Mac\s+OS\s+X)\s*(?:[;)]\s*([^)]+))?/);
          if (model && /iPhone ([6-9]|1[0-9])|iPad ([4-9]|1[0-9])/.test(model[1])) {
            os.bit = '64';
          } else {
            os.bit = '32';
          }
        } else {
          os.bit = '32';
        }
      }
    } else if (/Android/.test(userAgent)) {
      os.name = 'Android';
      const match = userAgent.match(/Android (\d+(\.\d+)*)/);
      os.version = match ? match[1] : '';
      os.bit = /x86_64|arm64/.test(userAgent) ? '64' : '32';
    } else if (/Windows NT (\d+\.\d+)/.test(userAgent)) {
      os.name = 'Windows';
      const ntVersion = userAgent.match(/Windows NT (\d+\.\d+)/)[1];
      // 转换 Windows NT 版本到常用名称
      const windowsVersions = {
        '10.0': '10',
        '6.3': '8.1',
        '6.2': '8',
        '6.1': '7',
        '6.0': 'Vista',
        '5.2': 'XP x64',
        '5.1': 'XP'
      };
      os.version = windowsVersions[ntVersion] || ntVersion;
      os.bit = /(Win64|WOW64|x64)/.test(userAgent) ? '64' : '32';
    } else if (/Mac OS X/.test(userAgent)) {
      os.name = 'Mac OS X';
      const match = userAgent.match(/Mac OS X (\d+_[\d_]+)/);
      os.version = match ? match[1].replace(/_/g, '.') : '';

      // macOS 从 Catalina (10.15) 开始只支持64位应用
      const versionNum = parseFloat(os.version.split('.').slice(0, 2).join('.'));
      if (versionNum >= 10.15) {
        os.bit = '64';
      } else {
        os.bit = /x86_64|arm64/.test(userAgent) ? '64' : '32';
      }
    } else if (/Linux/.test(userAgent)) {
      os.name = 'Linux';
      os.version = '';
      os.bit = /x86_64/.test(userAgent) ? '64' : '32';
    }

    return { browser, os, device };
  },

  /**
   * 通用数据分组工具函数
   * @param {Array} data - 要分组的数据源
   * @param {string} groupField - 用于分组的字段名
   * @param {Object} options - 配置选项
   * @returns {Object} 分组后的数据对象
   */
  groupBy(data, groupField) {
    const groups = {};

    data.forEach(item => {
      const groupKey = item[groupField];
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }

      const processedItem = { ...item };

      groups[groupKey].push(processedItem);
    });

    return groups;
  },

  /**
   * 将数据按指定字段分组并转换为数组格式
   * @param {Array} data - 要分组的数据源
   * @param {string} groupField - 用于分组的字段名
   * @returns {Array} 分组后的数组，格式为 [{provinceName: string, children: Array}]
   */
  groupByToArray(data, groupField) {
    const groups = this.groupBy(data, groupField);
    return Object.entries(groups).map(([key, value]) => {
      const municipalities = ['北京市', '上海市', '天津市', '重庆市', '通用规则'];

      // 检查是否为直辖市数据
      const isMunicipality = municipalities.includes(key);

      // 如果是直辖市，直接返回数据，不进行分组
      if (isMunicipality) {
        return value[0];
      }
      return {
        [groupField]: key,
        children: value
      }
    });
  }
};

export default utils;